import React, { CSSProperties, useEffect, useRef, useState } from 'react';
import { Animated, ImageBackground, ImageProps, ImageSourcePropType, StyleSheet, Image, StyleProp, ImageStyle, ViewStyle, ImageComponent, PixelRatio } from 'react-native';
import ImageClass, { ImageOptions } from '../Image';
import { isMobile } from '../utils/platformCheck';
import { sleep } from '../utils/sleep';
import { images } from '../../components/functions';

interface LoadImagesAtOncePropsSource {
    image: ImageSourcePropType;
    partials: React.ReactElement[] | undefined;
}
interface LoadImagesAtOnceProps {
    source: LoadImagesAtOncePropsSource | undefined,
    _key: string,
    style: any,
    canvasStyle?: CSSProperties,
    onLoadEnd: (image?: any) => void,
    onLoadStart: () => void,
    hoveredTriggered?: (image: string | null) => void;
    clickableItem?: (item?: string | null) => void;


}

export const LoadImagesAtOnce: React.FC<LoadImagesAtOnceProps & Omit<ImageProps, "style" | "source" | "onLoadEnd">> = React.memo(({ source, _key, style, canvasStyle, onLoadStart, onLoadEnd, hoveredTriggered, clickableItem, ...props }) => {
    // const loaded = useRef(false);r
    const { opacity, ...rest } = style;
    const previous = useRef<LoadImagesAtOncePropsSource>();
    const offscreenCanvases = useRef<Map<string, HTMLCanvasElement>>(new Map());
    const previousHitImageKey = useRef<string | null>(null);
    const [hoveredImage, setHoveredImage] = useState<string>();


    const canvasRef = useRef<HTMLCanvasElement>();
    const canvasContext = useRef<CanvasRenderingContext2D>();
    const [loaded, setLoaded] = useState(false);
    const partialsLoaded = useRef(false);
    const imageLoaded = useRef(false);

    useEffect(() => {
        if (!source) { return; }
        // if (source === previous.current) { return; }
        imageLoaded.current = false;
        setLoaded(false);
        // TODO: mobile won't trigger onload end for main image if equal thus never finishes
        if (isMobile()) {
            if ((source.image === previous.current?.image)) {
                setLoaded(true);
            } else {
                onLoadStart();
            }
        } else {
            onLoadStart();
            canvasContext.current = canvasRef.current?.getContext('2d') as CanvasRenderingContext2D;
            const image = new window.Image(1920, 1080);
            if (previous.current === source.image) {

                doCanvasLogic(source, image);
            } else {

                image.onload = async () => {
                    await doCanvasLogic(source, image);
                };
            }
            image.src = source.image as string;
        }
        previous.current = source;


    }, [source]);

    async function doCanvasLogic(source: LoadImagesAtOncePropsSource, image: HTMLImageElement) {
        if (props.blurRadius) {
            canvasContext.current!.filter = `blur(${props.blurRadius}px)`;

        }
        canvasContext.current?.drawImage(image, 0, 0, 1920, 1080);
        if (source.partials?.length) {
            for (const partial of source.partials) {
                const key = ImageClass._globalImages.get(partial.key as string)?.url || partial.props.source;
                await drawImage(key);
            }
        }
        canvasContext.current?.save();
        onLoadEnd(source?.image);
        // doWipeAnim(image);
    }

    const drawImage = (src: string): Promise<void> => {
        return new Promise((resolve, reject) => {
            const partialImage = new window.Image(1920, 1080);
            partialImage.onload = () => {
                canvasContext.current?.drawImage(partialImage, 0, 0, 1920, 1080);
                // hidden canvas
                const offScreenCanvas = document.createElement('canvas');
                offScreenCanvas.width = 1920;
                offScreenCanvas.height = 1080;
                const ctx = offScreenCanvas.getContext('2d');
                ctx?.drawImage(partialImage, 0, 0, 1920, 1080);
                offscreenCanvases.current.set(src, offScreenCanvas); // Store the canvas

                resolve();
            };
            partialImage.onerror = () => {
                console.error(`Failed to load image at ${src}`);
                resolve();
            };
            partialImage.src = src;
        });
    };

    useEffect(() => {
        if (loaded) {

            onLoadEnd();
        }

    }, [loaded]);

    function getPixelColor(event: React.MouseEvent<HTMLCanvasElement, MouseEvent>) {
        if (!clickableItem) { return; }
        const canvas = canvasRef.current;
        if (!canvas || !canvasContext.current) return;

        const rect = canvas.getBoundingClientRect();
        const scaleX = canvas.width / canvas.clientWidth;
        const scaleY = canvas.height / canvas.clientHeight;
        const canvasX = (event.clientX - rect.left) * scaleX;
        const canvasY = (event.clientY - rect.top) * scaleY;

        let hitImageKey: string | null = null;
        offscreenCanvases.current.forEach((offCanvas, key) => {
            const ctx = offCanvas.getContext('2d');
            const pixel = ctx?.getImageData(canvasX, canvasY, 1, 1).data;
            if (pixel && pixel[3] !== 0) { // Check if pixel is not transparent
                hitImageKey = key;
            }
        });
        if (!hitImageKey && previousHitImageKey.current) {
            setHoveredImage(undefined);
            hoveredTriggered?.(null);
            previousHitImageKey.current = null;
        }


        if (hitImageKey) {
            // tintImageGreen(hitImageKey);
            setHoveredImage(hitImageKey);
            hoveredTriggered?.(hitImageKey);
            previousHitImageKey.current = hitImageKey;
        }

    }





    function doWipeAnim(image: CanvasImageSource) {
        if (!image) {
            return;

        }
        var wipeProgress = 0;  // variable to track animation progress

        // calculate the desired animation duration in milliseconds
        var animationDuration = 1000;

        // calculate the distance to be covered in each animation frame
        var distancePerFrame = 1920 / (animationDuration / 16); // assuming 60 frames per second

        // function to update the animation frame
        function updateWipe() {
            wipeProgress += distancePerFrame;
            // , 0, 0, 1920, 1080
            canvasContext.current?.clearRect(0, 0, 1920, 1080); // clear canvas for new draw
            canvasContext.current?.drawImage(image, 0, 0, wipeProgress, 1080, 0, 0, wipeProgress, 1080); // clip and draw image up to wipeProgress
            canvasContext.current?.fillRect(wipeProgress, 0, 5, 1080); // draw a thin black bar at wipeProgress

            if (wipeProgress >= 1920) {
                clearInterval(animationInterval); // stop the animation once the entire canvas is wiped
            }
        }

        // start the animation interval
        var animationInterval = setInterval(updateWipe, 16);
    }
    return (
        <Animated.View renderToHardwareTextureAndroid needsOffscreenAlphaCompositing key={_key} style={[style, {}]}>
            <canvas ref={canvasRef as any} id="output" style={canvasStyle}

                width="1920" height="1080" aria-details={source?.image as any}
                onMouseMove={getPixelColor}
                onClick={() => {
                    clickableItem?.(previousHitImageKey.current);
                }}
            ></canvas>
            {hoveredImage ? <Image source={images.v1.testpc1.partials.testpc2.url} style={{ width: "100%", height: "100%", position: "absolute", pointerEvents: "none" } as any}></Image> : <></>}

        </Animated.View>
    );
});